import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
  Switch,
  SwitchGroup,
  SwitchLabel,
  Transition,
  TransitionChild,
} from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { CheckIcon, ChevronUpDownIcon } from '@heroicons/react/24/solid';
import React, { Fragment, useEffect, useState } from 'react';
import { useAccount } from '../../hooks/useAccount';
import { useUpdateSetting } from '../../hooks/useAccountMutations';
import { useFeatureFlag } from '../../hooks/useFeatureFlag';
import {
  AppSetting,
  BooleanSettingValue,
  getBooleanSetting,
  getNumberSetting,
  SocialDigestHourOption,
  SOCIAL_DIGEST_HOURS_OPTIONS,
} from '../../services/models/app-settings';
import { FeatureFlag } from '../../services/models/feature-flags';
import { classNames } from '../../utils/class-names';

interface SocialPreferencesPanelProps {
  panelOpen: boolean;
  onClose: () => void;
}

export default function SocialPreferencesPanel({
  panelOpen,
  onClose,
}: SocialPreferencesPanelProps) {
  const { account: currentAccount } = useAccount();
  const account = currentAccount!;
  const { updateSetting } = useUpdateSetting();
  const socialAlertsFlag = useFeatureFlag(FeatureFlag.ShowSocialAlerts);

  // Preferences
  const timezone =
    account.settings?.length > 0
      ? account.settings.find(x => x.name === AppSetting.Timezone)?.value
      : '';
  const [receiveSocialAlerts, setReceiveSocialAlerts] = useState(false);
  const [receiveSocialDigest, setReceiveSocialDigest] = useState(false);
  const [alertsMaxEmailsPerDay, setAlertsMaxEmailsPerDay] = useState(5);
  const [alertsSharesThreshold, setAlertsSharesThreshold] = useState(3);
  const [socialDigestHourOption, setSocialDigestHourOption] = useState(
    SOCIAL_DIGEST_HOURS_OPTIONS[9]
  );

  useEffect(() => {
    const getSocialDigestHourOption = () => {
      const accountSetting = account.settings?.find(x => x.name === AppSetting.SocialDigestHour);
      const socialDigestHour = !!accountSetting
        ? accountSetting.value
        : SOCIAL_DIGEST_HOURS_OPTIONS[9].value;
      return SOCIAL_DIGEST_HOURS_OPTIONS[Number(socialDigestHour)];
    };

    setReceiveSocialAlerts(getBooleanSetting(account.settings, AppSetting.ReceiveSocialAlerts));
    setReceiveSocialDigest(getBooleanSetting(account.settings, AppSetting.ReceiveSocialDigest));
    setAlertsMaxEmailsPerDay(
      getNumberSetting(account.settings, AppSetting.SocialAlertsMaxEmailsPerDay, 5)
    );
    setAlertsSharesThreshold(
      getNumberSetting(account.settings, AppSetting.SocialAlertsSharesThreshold, 3)
    );
    setSocialDigestHourOption(getSocialDigestHourOption());
  }, [account.settings]);

  const handleDigestHourChange = (option: SocialDigestHourOption) => {
    setSocialDigestHourOption(option);
    updateSetting(AppSetting.SocialDigestHour, option.value);
  };

  const handleReceiveDigestChange = (value: boolean) => {
    setReceiveSocialDigest(value);
    updateSetting(
      AppSetting.ReceiveSocialDigest,
      value ? BooleanSettingValue.Yes : BooleanSettingValue.No
    );
  };

  const handleReceiveAlertsChange = (value: boolean) => {
    setReceiveSocialAlerts(value);
    updateSetting(
      AppSetting.ReceiveSocialAlerts,
      value ? BooleanSettingValue.Yes : BooleanSettingValue.No
    );

    if (value) {
      updateSetting(AppSetting.SocialAlertsSharesThreshold, alertsSharesThreshold.toString());
      updateSetting(AppSetting.SocialAlertsMaxEmailsPerDay, alertsMaxEmailsPerDay.toString());
    }
  };

  const handleAlertsThresholdChange = (value: number) => {
    setAlertsSharesThreshold(value);
    updateSetting(AppSetting.SocialAlertsSharesThreshold, value.toString());
  };

  const handleAlertsMaxEmailsChange = (value: number) => {
    setAlertsMaxEmailsPerDay(value);
    updateSetting(AppSetting.SocialAlertsMaxEmailsPerDay, value.toString());
  };

  return (
    <Transition show={panelOpen} as={Fragment}>
      <Dialog as="div" className="fixed inset-0 overflow-hidden" onClose={onClose}>
        <div className="absolute inset-0 overflow-hidden">
          <TransitionChild
            as={Fragment}
            enter="ease-in-out duration-500"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in-out duration-500"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <DialogBackdrop className="absolute inset-0 bg-gray-500/75 transition-opacity" />
          </TransitionChild>
          <div className="fixed inset-y-0 right-0 pl-10 max-w-full flex">
            <TransitionChild
              as={Fragment}
              enter="transform transition ease-in-out duration-500 sm:duration-700"
              enterFrom="translate-x-full"
              enterTo="translate-x-0"
              leave="transform transition ease-in-out duration-500 sm:duration-700"
              leaveFrom="translate-x-0"
              leaveTo="translate-x-full"
            >
              <DialogPanel className="relative w-screen max-w-md">
                <TransitionChild
                  as={Fragment}
                  enter="ease-in-out duration-500"
                  enterFrom="opacity-0"
                  enterTo="opacity-100"
                  leave="ease-in-out duration-500"
                  leaveFrom="opacity-100"
                  leaveTo="opacity-0"
                >
                  <div className="absolute top-0 left-0 -ml-8 pt-4 pr-2 flex sm:-ml-10 sm:pr-4">
                    <button
                      type="button"
                      className="rounded-md text-gray-300 hover:text-white focus:outline-hidden focus:ring-2 focus:ring-white"
                      onClick={onClose}
                    >
                      <span className="sr-only">Close panel</span>
                      <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                    </button>
                  </div>
                </TransitionChild>
                <div className="h-full flex flex-col py-6 bg-white shadow-xl overflow-y-scroll">
                  <div className="mt-6 relative flex-1 px-4 sm:px-6">
                    <div className="space-y-1">
                      <h3 className="text-lg leading-6 font-medium text-gray-900">
                        Email Notifications
                      </h3>
                      <p className="max-w-2xl text-sm text-gray-500 m-0">
                        Manage how you get notify
                      </p>
                    </div>

                    <div className="mt-6">
                      <div className="divide-y divide-gray-200">
                        <SwitchGroup
                          as="div"
                          className="flex items-center justify-between py-4 sm:py-5"
                        >
                          <span className="grow flex flex-col">
                            <SwitchLabel
                              as="span"
                              className="text-sm font-medium text-gray-900"
                              passive
                            >
                              Receive daily digests
                            </SwitchLabel>
                          </span>
                          <Switch
                            checked={receiveSocialDigest}
                            onChange={handleReceiveDigestChange}
                            className={classNames(
                              receiveSocialDigest ? 'bg-indigo-600' : 'bg-gray-200',
                              'relative inline-flex shrink-0 h-6 w-11 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500'
                            )}
                          >
                            <span
                              aria-hidden="true"
                              className={classNames(
                                receiveSocialDigest ? 'translate-x-5' : 'translate-x-0',
                                'pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow-sm transform ring-0 transition ease-in-out duration-200'
                              )}
                            />
                          </Switch>
                        </SwitchGroup>

                        <div className="flex items-center justify-between py-4 sm:py-5">
                          <span className="grow flex flex-col">
                            <span className="text-sm font-medium text-gray-900">
                              Digest delivery time
                            </span>
                            <span className="text-sm text-gray-500">{timezone}</span>
                          </span>

                          <Listbox
                            value={socialDigestHourOption}
                            onChange={handleDigestHourChange}
                            disabled={!receiveSocialDigest}
                          >
                            {({ open }) => (
                              <>
                                <div className="mt-1 relative">
                                  <ListboxButton className="bg-white relative w-full border border-gray-300 rounded-md shadow-xs pl-3 pr-10 py-2 text-left cursor-default focus:outline-hidden focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm">
                                    <span className="block truncate">
                                      {socialDigestHourOption.label}
                                    </span>
                                    <span className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                                      <ChevronUpDownIcon
                                        className="h-5 w-5 text-gray-400"
                                        aria-hidden="true"
                                      />
                                    </span>
                                  </ListboxButton>

                                  <Transition
                                    show={open}
                                    as={Fragment}
                                    leave="transition ease-in duration-100"
                                    leaveFrom="opacity-100"
                                    leaveTo="opacity-0"
                                  >
                                    <ListboxOptions className="absolute z-10 mt-1 w-full bg-white shadow-lg max-h-60 rounded-md py-1 text-base ring-1 ring-black/5 overflow-auto focus:outline-hidden sm:text-sm">
                                      {SOCIAL_DIGEST_HOURS_OPTIONS.map(hourOption => (
                                        <ListboxOption
                                          key={hourOption.value}
                                          className={({ focus }) =>
                                            classNames(
                                              focus ? 'text-white bg-indigo-600' : 'text-gray-900',
                                              'cursor-default select-none relative py-2 pl-3 pr-9'
                                            )
                                          }
                                          value={hourOption}
                                        >
                                          {({ selected, focus }) => (
                                            <>
                                              <span
                                                className={classNames(
                                                  selected ? 'font-semibold' : 'font-normal',
                                                  'block truncate'
                                                )}
                                              >
                                                {hourOption.label}
                                              </span>

                                              {selected ? (
                                                <span
                                                  className={classNames(
                                                    focus ? 'text-white' : 'text-indigo-600',
                                                    'absolute inset-y-0 right-0 flex items-center pr-4'
                                                  )}
                                                >
                                                  <CheckIcon
                                                    className="h-5 w-5"
                                                    aria-hidden="true"
                                                  />
                                                </span>
                                              ) : null}
                                            </>
                                          )}
                                        </ListboxOption>
                                      ))}
                                    </ListboxOptions>
                                  </Transition>
                                </div>
                              </>
                            )}
                          </Listbox>
                        </div>

                        {socialAlertsFlag && (
                          <>
                            <SwitchGroup
                              as="div"
                              className="flex items-center justify-between py-4 sm:py-5"
                            >
                              <span className="grow flex flex-col">
                                <SwitchLabel
                                  as="span"
                                  className="text-sm font-medium text-gray-900"
                                  passive
                                >
                                  Receive news alerts
                                </SwitchLabel>
                              </span>
                              <Switch
                                checked={receiveSocialAlerts}
                                onChange={handleReceiveAlertsChange}
                                className={classNames(
                                  receiveSocialAlerts ? 'bg-indigo-600' : 'bg-gray-200',
                                  'relative inline-flex shrink-0 h-6 w-11 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500'
                                )}
                              >
                                <span
                                  aria-hidden="true"
                                  className={classNames(
                                    receiveSocialAlerts ? 'translate-x-5' : 'translate-x-0',
                                    'pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow-sm transform ring-0 transition ease-in-out duration-200'
                                  )}
                                />
                              </Switch>
                            </SwitchGroup>

                            <div className="flex items-center justify-between py-4 sm:py-5">
                              <span className="grow flex flex-col">
                                <span className="text-sm font-medium text-gray-900">
                                  Alert you when shared by
                                </span>
                              </span>

                              <Listbox
                                value={alertsSharesThreshold}
                                onChange={handleAlertsThresholdChange}
                                disabled={!receiveSocialAlerts}
                              >
                                {({ open }) => (
                                  <>
                                    <div className="mt-1 relative">
                                      <ListboxButton className="bg-white relative w-full border border-gray-300 rounded-md shadow-xs pl-3 pr-10 py-2 text-left cursor-default focus:outline-hidden focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm">
                                        <span className="block truncate">
                                          {alertsSharesThreshold} people
                                        </span>
                                        <span className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                                          <ChevronUpDownIcon
                                            className="h-5 w-5 text-gray-400"
                                            aria-hidden="true"
                                          />
                                        </span>
                                      </ListboxButton>

                                      <Transition
                                        show={open}
                                        as={Fragment}
                                        leave="transition ease-in duration-100"
                                        leaveFrom="opacity-100"
                                        leaveTo="opacity-0"
                                      >
                                        <ListboxOptions className="absolute z-10 mt-1 w-full bg-white shadow-lg max-h-60 rounded-md py-1 text-base ring-1 ring-black/5 overflow-auto focus:outline-hidden sm:text-sm">
                                          {[3, 4, 5, 6, 7, 8, 9, 10].map(shareThreshold => (
                                            <ListboxOption
                                              key={shareThreshold}
                                              className={({ focus }) =>
                                                classNames(
                                                  focus
                                                    ? 'text-white bg-indigo-600'
                                                    : 'text-gray-900',
                                                  'cursor-default select-none relative py-2 pl-3 pr-9'
                                                )
                                              }
                                              value={shareThreshold}
                                            >
                                              {({ selected, focus }) => (
                                                <>
                                                  <span
                                                    className={classNames(
                                                      selected ? 'font-semibold' : 'font-normal',
                                                      'block truncate'
                                                    )}
                                                  >
                                                    {shareThreshold} people
                                                  </span>

                                                  {selected ? (
                                                    <span
                                                      className={classNames(
                                                        focus ? 'text-white' : 'text-indigo-600',
                                                        'absolute inset-y-0 right-0 flex items-center pr-4'
                                                      )}
                                                    >
                                                      <CheckIcon
                                                        className="h-5 w-5"
                                                        aria-hidden="true"
                                                      />
                                                    </span>
                                                  ) : null}
                                                </>
                                              )}
                                            </ListboxOption>
                                          ))}
                                        </ListboxOptions>
                                      </Transition>
                                    </div>
                                  </>
                                )}
                              </Listbox>
                            </div>

                            <div className="flex items-center justify-between py-4 sm:py-5">
                              <span className="grow flex flex-col">
                                <span className="text-sm font-medium text-gray-900">
                                  Max number of alerts per day
                                </span>
                              </span>

                              <Listbox
                                value={alertsMaxEmailsPerDay}
                                onChange={handleAlertsMaxEmailsChange}
                                disabled={!receiveSocialAlerts}
                              >
                                {({ open }) => (
                                  <>
                                    <div className="mt-1 relative">
                                      <ListboxButton className="bg-white relative w-full border border-gray-300 rounded-md shadow-xs pl-3 pr-10 py-2 text-left cursor-default focus:outline-hidden focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm">
                                        <span className="block truncate">
                                          {alertsMaxEmailsPerDay} alerts
                                        </span>
                                        <span className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                                          <ChevronUpDownIcon
                                            className="h-5 w-5 text-gray-400"
                                            aria-hidden="true"
                                          />
                                        </span>
                                      </ListboxButton>

                                      <Transition
                                        show={open}
                                        as={Fragment}
                                        leave="transition ease-in duration-100"
                                        leaveFrom="opacity-100"
                                        leaveTo="opacity-0"
                                      >
                                        <ListboxOptions className="absolute z-10 mt-1 w-full bg-white shadow-lg max-h-60 rounded-md py-1 text-base ring-1 ring-black/5 overflow-auto focus:outline-hidden sm:text-sm">
                                          {Array.from(Array(20)).map((_, numEmails) => (
                                            <ListboxOption
                                              key={numEmails}
                                              className={({ focus }) =>
                                                classNames(
                                                  focus
                                                    ? 'text-white bg-indigo-600'
                                                    : 'text-gray-900',
                                                  'cursor-default select-none relative py-2 pl-3 pr-9'
                                                )
                                              }
                                              value={numEmails + 1}
                                            >
                                              {({ selected, focus }) => (
                                                <>
                                                  <span
                                                    className={classNames(
                                                      selected ? 'font-semibold' : 'font-normal',
                                                      'block truncate'
                                                    )}
                                                  >
                                                    {numEmails + 1} alerts
                                                  </span>

                                                  {selected ? (
                                                    <span
                                                      className={classNames(
                                                        focus ? 'text-white' : 'text-indigo-600',
                                                        'absolute inset-y-0 right-0 flex items-center pr-4'
                                                      )}
                                                    >
                                                      <CheckIcon
                                                        className="h-5 w-5"
                                                        aria-hidden="true"
                                                      />
                                                    </span>
                                                  ) : null}
                                                </>
                                              )}
                                            </ListboxOption>
                                          ))}
                                        </ListboxOptions>
                                      </Transition>
                                    </div>
                                  </>
                                )}
                              </Listbox>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
